package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.expr.ExprBlobReader
import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.dsl.ir.model.hud.HudMetaDefaults
import top.wcpe.allincore.ui.runtime.element.FontStyle
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.KeyEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.host.ToastManager
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.concurrent.AtomicBooleanFlag
import top.wcpe.allincore.ui.runtime.internal.concurrent.AtomicLongCounter
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.api.ClientApi
import top.wcpe.allincore.ui.runtime.ir.api.KeyBindingHost
import top.wcpe.allincore.ui.runtime.ir.api.PlaceholderApi
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.request.RequestDispatcher
import top.wcpe.allincore.ui.runtime.ir.request.RequestRegistry
import top.wcpe.allincore.ui.runtime.ir.subscribe.Subscription
import top.wcpe.allincore.ui.runtime.reactive.Signal
import top.wcpe.allincore.ui.runtime.render.RendererRegistry
import top.wcpe.allincore.ui.runtime.theme.ThemeEnvironment
import top.wcpe.allincore.ui.runtime.theme.ThemeLookup
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmName

/**
 * 一次解释结果的快照：根元素 + 内部上下文，便于外部驱动 action / 投递回调 / 应用 state patch。
 *
 * 各运行时对象通过 Kotlin 属性暴露（Java 侧仍是 `getRoot()` / `getStates()` 等访问器，ABI 不变）。
 */
@Suppress("TooManyFunctions", "TooGenericExceptionCaught", "ReturnCount", "LongParameterList")
class IrSession internal constructor(
    val root: UIElement<*>,
    val states: Map<String, Signal<Any?>>,
    /**
     * M5-c #I5：state 字段名 → 静态类型映射。
     *
     * 仅包含 DSL 显式标注类型的字段；patch / action 写入时据此做 runtime guard。
     */
    val stateTypes: Map<String, IrType>,
    val actions: Map<String, List<StmtNode>>,
    val evalContext: EvalContext,
    val registry: RequestRegistry,
    val requestDispatcher: RequestDispatcher,
    /**
     * screen 级 onKey lambda；DSL 中通过 `onKey { event -> ... }` 声明，未声明时为 null。
     *
     * UIRuntimeScreen.keyPressed 在焦点元素未消费按键时调用本 handler。
     */
    val screenOnKey: UiEventHandler<KeyEvent>?,
    /**
     * screen 级 onKeyRelease lambda；DSL 中通过 `onKeyRelease { event -> ... }` 声明，未声明时为 null。
     *
     * 与 [screenOnKey] 严格分离：onKey 仅 PRESS、onKeyRelease 仅 RELEASE。历史实现把 RELEASE 也派给
     * onKey，导致 `if (event.key == "E") { local.close() }` 这类常见写法在松开时被误触发，造成
     * "按 E 关背包后 vanilla 又重新打开"的循环 bug。引入独立钩子分离两种语义。
     */
    val screenOnKeyRelease: UiEventHandler<KeyEvent>?,
    /**
     * RFC-INPUT-EVENT-UNIFICATION Stage 4：screen 级 onMouseDown lambda；未声明时为 null。
     *
     * UIRuntimeScreen.mouseClicked 在元素 onClick / 元素 hotkey / SlotEventBridge 均未消费鼠标按下时
     * 调用本 handler。与 [screenOnKey] 完全对称。
     */
    val screenOnMouseDown: UiEventHandler<MouseEvent>?,
    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage A：screen 级 onMouseUp lambda；未声明时为 null。
     *
     * UIRuntimeScreen.mouseReleased 在平台默认释放逻辑之后调用本 handler，与 [screenOnMouseDown] 保持对称。
     */
    val screenOnMouseUp: UiEventHandler<MouseEvent>?,
    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage B：screen 级 onMouseMove lambda；未声明时为 null。
     *
     * UIRuntimeScreen.mouseMoved 在平台鼠标移动入口调用本 handler，与 [screenOnMouseUp] 保持对称。
     */
    val screenOnMouseMove: UiEventHandler<MouseEvent>?,
    /** M6-h Round 2：与 session 同生命周期的 toast 管理器；session 销毁前调用 dispose。 */
    val toastManager: ToastManager,
    /** 屏幕 open 块体；DSL 通过 `open { ... }` 声明，未声明时为 null。 */
    val openBody: List<StmtNode>?,
    /** 屏幕 close 块体；DSL 通过 `close { ... }` 声明，未声明时为 null。 */
    val closeBody: List<StmtNode>?,
    /** 屏幕 show 块体；DSL 通过 `show { ... }` 声明，未声明时为 null。每次界面显示都执行。 */
    val showBody: List<StmtNode>?,
    /** 屏幕 hide 块体；DSL 通过 `hide { ... }` 声明，未声明时为 null。常驻界面每次隐藏都执行。 */
    val hideBody: List<StmtNode>?,
    autoSubscriptions: List<Subscription>? = emptyList(),
    /** Stage 7：HUD tick 块体；DSL 通过 `tick { ... }` 声明，未声明时为 null。 */
    val tickBody: List<StmtNode>? = null,
    keyBindingHost: KeyBindingHost? = KeyBindingHost.NOOP,
) {

    /**
     * B7：IR 中声明的静态订阅在 interpretSession 时自动注册的句柄列表；
     * dispose 时统一 cancel，保证 session 销毁后不再收到消息。仅供测试验证。
     */
    val autoSubscriptions: List<Subscription> =
        if (autoSubscriptions == null) emptyList() else autoSubscriptions.toList()

    /**
     * Phase 4：HUD keybinding 槽位管理器；session 创建时注入，销毁时释放所有槽位。
     *
     * HUD keybinding 块在 IrInterpreter.interpretSession 中注册到槽位池；
     * 每帧 tick 调用 [tickKeyBindings] 检测触发并执行 onTrigger。
     */
    private val keyBindingHost: KeyBindingHost = keyBindingHost ?: KeyBindingHost.NOOP

    /** 上次 tick 触发时间戳（毫秒），用于 50ms 节流；首帧前为 0。 */
    private val lastTickAtMs = AtomicLongCounter(0L)

    /** Phase 4：已分配槽位 ID 列表（用于 session 销毁时批量释放）。 */
    private val allocatedSlots = ArrayList<String>()

    /**
     * 用于 IrInterpreter 串行化屏幕生命周期：标记 open 块是否已调用过。
     * 首次进入屏幕时执行 open 块；后续 patch 不重复执行。
     */
    private val openInvoked = AtomicBooleanFlag(false)

    /** session 级暂停标记，保证 pause/resume 幂等。 */
    @Volatile
    private var sessionPaused = false

    /** dispose 守卫：保证同一 session 多次调用 dispose 只有第一次真正触发。 */
    private val disposed = AtomicBooleanFlag(false)

    /**
     * screen 标识符，仅用于日志定位（"screen open 块执行异常 [screenId=xxx]"）。
     * 由 IrInterpreter.interpretSession 在构造完成后一次性写入；之后只读。
     * 写入采用首次有效语义：仅当当前为 null 时才接受写入，避免误覆盖。
     */
    @Volatile
    var screenId: String? = null
        set(value) {
            if (field == null) {
                field = value
            }
        }

    /**
     * 界面级默认字体（对应 DSL 顶层 `font { ... }` 块）；未声明时为 null。
     * 由 IrInterpreter.interpretSession 在构造完成后注入；[bindRendererRegistry] 时写入 RendererRegistry.pageFont。
     */
    @Volatile
    var pageFont: FontStyle? = null

    /** 当前会话独立的响应式主题环境。 */
    val themeEnvironment: ThemeEnvironment get() = evalContext.themeEnvironment

    /** 暴露当前 IR 的主题 token 表，便于客户端创建 RenderRoot 时注入主题。 */
    val themeLookup: ThemeLookup get() = themeEnvironment.lookup()

    /** 派生一个替换 EvalContext 的 session，其余运行时对象保持共享。 */
    fun withEvalContext(nextEvalContext: EvalContext): IrSession {
        val derived = IrSession(
            root, states, stateTypes, actions, nextEvalContext, registry,
            requestDispatcher, screenOnKey, screenOnKeyRelease, screenOnMouseDown, screenOnMouseUp,
            screenOnMouseMove, toastManager, openBody, closeBody, showBody, hideBody,
            autoSubscriptions, tickBody, keyBindingHost,
        )
        // keybinding 槽位在 interpretSession 时已登记到本 session 的 allocatedSlots；派生 session
        // （withPlaceholderApi / 注入 client API 等）必须继承这些槽位 ID，否则：① 派生后才是真正被
        // HudRegistry/activeSessionData 持有并每帧 tick 的 session，其 allocatedSlots 为空会导致
        // tickKeyBindings 跳过轮询，keybinding 永不触发；② dispose 派生 session 时不会释放槽位，
        // 造成 vanilla KeyMapping 在 HUD/屏幕关闭后泄漏。slotId 仅做登记引用，复制到派生 session 安全。
        derived.allocatedSlots.addAll(allocatedSlots)
        return derived
    }

    /** 派生一个注入 placeholder API 的 session。 */
    fun withPlaceholderApi(placeholderApi: PlaceholderApi): IrSession =
        withEvalContext(evalContext.withPlaceholderApi(placeholderApi))

    /**
     * 调度 open 块体执行。
     *
     * 同一 session 多次调用只触发一次（首次进入后由 [openInvoked] 守门）。
     * 若 EvalContext 持有 DslExecutor，提交到该 executor 异步执行；否则降级到调用线程
     * （保留与测试同步模式的兼容性）。任何异常被拦截并记录 warn 日志，不向调用方抛出。
     */
    fun runOpenIfPresent() {
        val body = openBody
        if (body.isNullOrEmpty()) return
        if (!openInvoked.compareAndSet(false, true)) return
        val task = UiTask {
            try {
                ExprEvaluator.execute(body, evalContext)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] screen open 块执行异常 [screenId=$screenId]", t)
            }
        }
        val executor = evalContext.executor
        if (executor != null) {
            executor.submit(task)
        } else {
            task.run()
        }
    }

    /**
     * 同步执行 close 块体。
     *
     * 必须在调用线程同步跑完，平台层据此保证销毁帧上的副作用按时序到位。
     * close 体内禁止 await / delay / net.request / state 写入 / local.* / 调 suspend fun
     * （由 DSL 编译期语义检查器拒绝）。任何异常被拦截并记录 warn 日志，不向调用方抛出。
     */
    fun runCloseIfPresent() {
        val body = closeBody
        if (body.isNullOrEmpty()) return
        try {
            ExprEvaluator.execute(body, evalContext)
        } catch (t: Throwable) {
            LOGGER.warn("[ui] screen close 块执行异常 [screenId=$screenId]", t)
        }
    }

    /**
     * 调度 show 块体执行。
     *
     * 每次界面显示都调用（首次打开 + 常驻每次重开），无 once 守门；语义同 open（异步提交、允许 await）。
     * 异常拦截记 warn、不外抛。
     */
    fun runShowIfPresent() {
        runLifecycleAsync(showBody, "show")
    }

    /**
     * 调度 hide 块体执行。
     *
     * 常驻界面每次隐藏都调用，无守门；语义同 show（异步）。
     */
    fun runHideIfPresent() {
        runLifecycleAsync(hideBody, "hide")
    }

    /** show / hide 共用：异步提交块体执行（无 once 守门），异常隔离记 warn。 */
    private fun runLifecycleAsync(body: List<StmtNode>?, label: String) {
        if (body.isNullOrEmpty()) return
        val task = UiTask {
            try {
                ExprEvaluator.execute(body, evalContext)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] screen $label 块执行异常 [screenId=$screenId]", t)
            }
        }
        val executor = evalContext.executor
        if (executor != null) {
            executor.submit(task)
        } else {
            task.run()
        }
    }

    /**
     * Stage 7：HUD tick 块每 50ms 节流执行一次，由平台层每帧调用。
     *
     * 策略 1=B：固定 50ms（≈vanilla 1 tick）节流，避免高帧率重复执行；
     * 策略 2=B：与可见性解耦，HUD 即使被 visibility hide 仍持续 tick。
     *
     * @param nowMs 当前时刻（毫秒），通常为 `currentTimeMillis()`
     */
    fun runTickIfDue(nowMs: Long) {
        val body = tickBody
        if (body.isNullOrEmpty()) return
        val last = lastTickAtMs.get()
        if (nowMs - last < HudMetaDefaults.TICK_INTERVAL_MS) return
        if (!lastTickAtMs.compareAndSet(last, nowMs)) return
        try {
            ExprEvaluator.execute(body, evalContext)
        } catch (t: Throwable) {
            LOGGER.warn("[ui] hud tick 块执行异常 [screenId=$screenId]", t)
        }
    }

    /**
     * Phase 4：每帧检测 HUD keybinding 触发并执行 onTrigger。
     *
     * 由平台层在每帧 tick 中调用（与 [runTickIfDue] 独立，无节流限制）。
     * 从 [keyBindingHost] 轮询触发的 onTrigger 字节码，反序列化并执行。
     */
    fun tickKeyBindings() {
        if (keyBindingHost == KeyBindingHost.NOOP) {
            return
        }
        // 本 session 未注册任何 keybinding 时直接返回：既省去无谓轮询，也避免在共享的全局
        // host 上误消费其它 session 的槽位触发（pollTriggers 仅轮询本 session 持有的槽位）。
        if (allocatedSlots.isEmpty()) return
        val triggers = keyBindingHost.pollTriggers(allocatedSlots)
        if (triggers.isNullOrEmpty()) return
        for (bytes in triggers) {
            try {
                val stmts = ExprBlobReader.readActionBody(bytes)
                ExprEvaluator.execute(stmts, evalContext)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] keybinding onTrigger 执行异常 [screenId=$screenId]", t)
            }
        }
    }

    /**
     * Phase 4：注册单个 keybinding 到槽位池。
     *
     * @param bindingId      DSL 中声明的 keybinding id
     * @param defaultKey     默认按键（如 "P"）；可空，省略表示未绑定（见 hud.md §1.5）
     * @param label          ControlsScreen 显示名称；可空，省略表示无显示名
     * @param onTriggerBytes onTrigger 字节码
     */
    @JvmName("registerKeyBinding")
    internal fun registerKeyBinding(bindingId: String, defaultKey: String?, label: String?, onTriggerBytes: ByteArray) {
        if (keyBindingHost == KeyBindingHost.NOOP) {
            return
        }
        val slotId = keyBindingHost.allocateSlot(bindingId, defaultKey, label, onTriggerBytes)
        if (slotId != null) {
            allocatedSlots.add(slotId)
        }
    }

    /**
     * 取消所有未完成的 await 请求。
     *
     * 在屏幕销毁路径上调用：open 块内若仍有 `await net.request` 等待回包，销毁后回包到达时若直接
     * 尝试恢复会引用已释放的 EvalContext 资源，必须主动取消。内部转调 [RequestRegistry.cancelAll]，
     * 未到达的回包后续被静默丢弃。
     */
    fun cancelPendingAwaits() {
        registry.cancelAll()
    }

    /** 释放 session 持有的可关闭资源（自动订阅 + ToastManager 的调度线程 + TimerHost + KeyBindingHost 槽位）。 */
    fun dispose() {
        if (!disposed.compareAndSet(false, true)) {
            return
        }
        // Phase 4：释放所有已分配的 KeyMapping 槽位
        for (slotId in allocatedSlots) {
            try {
                keyBindingHost.releaseSlot(slotId)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] 释放 keybinding 槽位 $slotId 时异常", t)
            }
        }
        allocatedSlots.clear()
        // 任务 4：先 cancel 所有 timer，避免后续触发时访问已清理的 evalContext。
        // 与 autoSubscriptions 同款"先关闭异步源"思路。
        try {
            evalContext.timerHost?.dispose()
        } catch (t: Throwable) {
            LOGGER.warn("[ui] dispose 时取消 TimerHost 异常", t)
        }
        for (sub in autoSubscriptions) {
            try {
                sub.cancel()
            } catch (t: Throwable) {
                LOGGER.warn("[ui] 取消自动订阅时异常", t)
            }
        }
        toastManager.dispose()
    }

    /**
     * 暂停 session 持有的可暂停资源（TimerHost + ToastManager）。
     * 自动订阅（autoSubscriptions）保持活跃，suspend 期间服务端推送仍可到达。
     * 幂等：重复调用安全。
     */
    fun pause() {
        if (sessionPaused) return
        sessionPaused = true
        try {
            evalContext.timerHost?.pause()
        } catch (t: Throwable) {
            LOGGER.warn("[ui] pause 时暂停 TimerHost 异常", t)
        }
        try {
            toastManager.pause()
        } catch (t: Throwable) {
            LOGGER.warn("[ui] pause 时暂停 ToastManager 异常", t)
        }
    }

    /**
     * 恢复 session 持有的已暂停资源（TimerHost + ToastManager）。
     * 幂等：未暂停时调用无副作用。
     */
    fun resume() {
        if (!sessionPaused) return
        sessionPaused = false
        try {
            evalContext.timerHost?.resume()
        } catch (t: Throwable) {
            LOGGER.warn("[ui] resume 时恢复 TimerHost 异常", t)
        }
        try {
            toastManager.resume()
        } catch (t: Throwable) {
            LOGGER.warn("[ui] resume 时恢复 ToastManager 异常", t)
        }
    }

    /**
     * 重置 open 块执行守门，允许 [runOpenIfPresent] 再次触发。
     * 仅用于需要重新执行 open 块的特殊场景，常规 suspend/resume 不调用。
     */
    fun resetOpenGate() {
        openInvoked.set(false)
    }

    /**
     * 绑定渲染器注册表：客户端创建 RenderRoot 后调用一次，
     * 让 `local.setTheme` 能写入 controlTheme 实现运行时主题切换。
     *
     * 转调 [EvalContext.bindRendererRegistry]。
     */
    fun bindRendererRegistry(registry: RendererRegistry) {
        evalContext.bindRendererRegistry(registry)
        // 界面级默认字体写入页面层：UIRenderer.render 会把它压入 FontAccess.setPage，
        // 经环境解析作用于本界面所有文字（屏幕与 HUD 渲染路径都经由此处绑定）。
        val pf = pageFont
        if (pf != null) {
            registry.pageFont(pf)
        }
    }

    /**
     * v4 §E.3：注入 `client` 作用域 API（`client.world.*` 数据源）。
     *
     * 客户端平台层（fabric / neoforge）在 session 创建后调用一次，传入包裹真实 WorldDataSource 的
     * [ClientApi]；未调用时 EvalContext 持有 [ClientApi.NOOP]，DSL 读 `client.world.posX` 得 0、
     * `entities(48)` 得空列表。转调 [EvalContext.attachClientApi]。
     */
    fun bindClientApi(clientApi: ClientApi) {
        evalContext.attachClientApi(clientApi)
    }

    companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.IrSession")
    }
}
